/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { ChevronUpPrimary } from '@ahamove/icons';
import { AddPrimary, BoxBlue, Weight } from '@ahamove/icons/order';
import {
  DeliveryType,
  SpecialRequestType,
  type BillableWeightRequestBody,
  type BillableWeightResponse,
  type IntercityPackageForm,
} from '@ahm/api-wrappers-core';
import {
  cleanObject,
  formatCurrency,
  formatNumber,
  parseSafeValue,
} from '@ahm/common-helpers';
import { useLocationStore } from '@ahm/react-map/store';
import { FloatingLabelInput, FloatingLabelNumberInput } from '@ahm/react-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  AccordionChevron,
  ActionIcon,
  Button,
  Card,
  Center,
  Container,
  Divider,
  Grid,
  Group,
  NumberInput,
  rem,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Space,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { openContextModal, type ContextModalProps } from '@mantine/modals';
import { Minus, Plus } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { ModalName, openCustomConfirmModal } from '..';
import InfoTooltip from '@/components/info-tooltip';
import { UploadPhoto } from '@/components/upload-photo';
import { useEstimateBillableWeight } from '@/features/booking/api/intercity';
import { DEFAULT_PACKAGE_DETAIL_ITEM } from '@/features/booking/components/booking-package-details';
import { assetPrefix } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { Link } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export type PackageDetailsModalProps = Pick<
  ContextModalProps<{
    initialData: IntercityPackageForm;
  }>,
  'innerProps'
> & {
  onClose?: () => void;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function PackageDetailsModal({
  onClose,
  innerProps: {
    initialData = { package_detail: [DEFAULT_PACKAGE_DETAIL_ITEM] },
  },
}: PackageDetailsModalProps) {
  const tCommon = useTranslations('common');
  const tActions = useTranslations('common.actions');
  const tPackageDetails = useTranslations('booking.package_details');
  const {
    billableWeightData,
    selectedService,
    setPackageDetail,
    setPackageImages,
    getTotalLengthWidthHeight,
    setBillableWeightData,
    pathInfo,
    setPathInfoByIndex,
  } = useBookingStore((state) => state);

  const [estBillableWeightData, setEstBillableWeightData] =
    useState<BillableWeightResponse | null>(billableWeightData);
  const isMobile = useIsMobile();
  const paths = useLocationStore.use.paths();

  const nationWidePathInfo = useMemo(() => {
    return pathInfo?.[1];
  }, [pathInfo]);

  const prevEstimateRequestBodyRef = useRef<string | null>(null);

  const request = selectedService?.requests?.find((request) =>
    request._id.includes(SpecialRequestType.Insurance)
  );

  const validationSchema = z.object({
    package_detail: z.array(
      z.object({
        code: z.string().optional(),
        name: z.string().optional(),
        weight: z
          .number({
            required_error: tPackageDetails('errors.weight.require'),
            invalid_type_error: tPackageDetails('errors.weight.require'),
          })
          .min(0.001, tPackageDetails('errors.weight.limit'))
          .max(50, tPackageDetails('errors.weight.limit')),
        height: z
          .number({
            required_error: tPackageDetails('errors.height'),
            invalid_type_error: tPackageDetails('errors.height'),
          })
          .max(300, tPackageDetails('errors.package_size.max'))
          .min(1, tPackageDetails('errors.package_size.min')),
        width: z
          .number({
            required_error: tPackageDetails('errors.width'),
            invalid_type_error: tPackageDetails('errors.width'),
          })
          .max(300, tPackageDetails('errors.package_size.max'))
          .min(1, tPackageDetails('errors.package_size.min')),
        length: z
          .number({
            required_error: tPackageDetails('errors.length'),
            invalid_type_error: tPackageDetails('errors.length'),
          })
          .max(300, tPackageDetails('errors.package_size.max'))
          .min(1, tPackageDetails('errors.package_size.min')),
        quantity: z
          .number({
            required_error: tPackageDetails('errors.quantity'),
            invalid_type_error: tPackageDetails('errors.quantity'),
          })
          .min(1, tPackageDetails('errors.quantity'))
          .max(100, tPackageDetails('errors.quantity_max')),
      })
    ),
    item_order_id: z.string().optional(), // tracking_number
    item_name: z.string().optional(),
    item_value: z
      .number()
      .optional()
      .nullable()
      .refine(
        (value) => {
          if (!value || !request?.max_item_value) return true;

          return value && request && value <= request.max_item_value;
        },
        {
          message: `${tPackageDetails('errors.max')} ${formatCurrency(request?.max_item_value)}`,
        }
      ),
    item_description: z.string().optional(), // item_type
    package_images: z.array(z.string()).optional(),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
    setValue,
    getValues,
  } = useForm<IntercityPackageForm>({
    resolver: zodResolver(validationSchema),
    defaultValues: initialData,
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    name: 'package_detail',
    control,
  });

  const packageDetails = useWatch({
    name: 'package_detail',
    control,
  });

  const [debouncedPackageDetails] = useDebouncedValue(packageDetails, 500);

  const invalidPackageDetails = useMemo(() => {
    return Boolean(
      debouncedPackageDetails.some(
        (item) =>
          !item.weight ||
          !item.height ||
          !item.width ||
          !item.length ||
          !item.quantity
      )
    );
  }, [debouncedPackageDetails, isValid]);

  const totalLengthWidthHeight = useMemo(
    () => getTotalLengthWidthHeight(debouncedPackageDetails),
    [debouncedPackageDetails, getTotalLengthWidthHeight]
  );

  const estimateRequestBody = useMemo(() => {
    if (
      invalidPackageDetails ||
      !isValid ||
      paths.length < 2 ||
      !selectedService?._id
    )
      return;

    return {
      path: paths,
      service: {
        _id: selectedService._id,
      },
      package_detail: debouncedPackageDetails,
    } as BillableWeightRequestBody;
  }, [
    paths,
    selectedService?._id,
    debouncedPackageDetails,
    invalidPackageDetails,
  ]);

  const [isEstimating, setIsEstimating] = useState<boolean>(false);
  const [estimateError, setEstimateError] = useState<boolean>(false);

  const { mutate: onEstimateBillableWeight } = useEstimateBillableWeight({
    onSuccess: (res) => {
      setEstimateError(false);
      setIsEstimating(false);
      setEstBillableWeightData(res);
      // setBillableWeightData(res);
    },
    onError: (error) => {
      setEstimateError(true);
      setIsEstimating(false);
      setEstBillableWeightData(null);
      // setBillableWeightData(null);

      const errorData = error?.response?.data;

      if (errorData) {
        openCustomConfirmModal({
          title: tCommon('something_went_wrong'),
          children:
            errorData.description ||
            errorData.title ||
            tCommon('something_went_wrong'),
          confirmLabel: tActions('ok_got_it'),
        });
      }
    },
  });

  useEffect(() => {
    const currentDependencies = JSON.stringify(estimateRequestBody);

    if (
      prevEstimateRequestBodyRef.current !== currentDependencies &&
      estimateRequestBody
    ) {
      setIsEstimating(true);
      onEstimateBillableWeight(estimateRequestBody);
    }

    prevEstimateRequestBodyRef.current = currentDependencies;
  }, [onEstimateBillableWeight, estimateRequestBody]);

  const [scrollPosition, onScrollPositionChange] = useState({ x: 0, y: 0 });
  const viewportRef = useRef<HTMLDivElement>(null);

  const [activeAccordion, setActiveAccordion] = useState<string[]>([
    fields[0]?.id,
  ]);

  const handleToggleAccordion = () => {
    const fieldIds = fields.map((f) => f.id);

    setActiveAccordion((prev) => {
      if (prev.length > 0) {
        return [];
      }
      return fieldIds;
    });
  };

  const scrollToBottomOfPackages = () => {
    viewportRef.current?.scrollTo({
      top: viewportRef.current!.scrollHeight,
      behavior: 'smooth',
    });
  };

  const handleAddPackageItem = () => {
    append(DEFAULT_PACKAGE_DETAIL_ITEM, {
      shouldFocus: true,
    });
  };

  const onSubmit = (data: IntercityPackageForm) => {
    const { package_detail, package_images, ...stopPointInfo } = data;
    setPackageDetail(package_detail);

    if (estBillableWeightData) {
      setBillableWeightData(estBillableWeightData);
    }
    if (package_images) {
      setPackageImages(package_images);
    }

    const shouldUpdateStopPoint = Object.values(stopPointInfo).some(
      (v) => v !== undefined
    );

    if (
      shouldUpdateStopPoint &&
      selectedService?.delivery_type === DeliveryType.Nationwide
    ) {
      setPathInfoByIndex(
        '1',
        nationWidePathInfo
          ? { ...nationWidePathInfo, ...cleanObject(stopPointInfo) }
          : cleanObject(stopPointInfo)
      );
    }

    onClose && onClose();
    reset(data);
  };

  useEffect(() => {
    if (fields.length > 0) {
      setActiveAccordion((prev) => {
        return [...prev, fields[fields.length - 1].id];
      });

      setTimeout(() => {
        scrollToBottomOfPackages();
      }, 500);
    }
  }, [fields]);

  // collapse all when open package-details-modal
  useEffect(() => {
    if (fields && fields.length > 1) {
      setActiveAccordion([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container
      size="xl"
      styles={{ root: { overflowX: 'hidden' } }}
      mx={{ base: rem(-16), sm: 'auto' }}
      px={{ base: '0', md: rem(8) }}
      pt={{ base: '0', md: rem(32) }}
      pb={{ base: rem(200 + 16), md: rem(16) }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid gutter={{ base: 'md', md: rem(24) }}>
          <Grid.Col span={{ base: 12, sm: 'auto' }}>
            <Card
              radius="md"
              p={0}
              mah={{ base: '100%', sm: 'calc(100dvh - 128px)' }}
            >
              <Group
                p="md"
                align="center"
                wrap="nowrap"
                gap={rem(8)}
                justify="space-between"
                styles={{
                  root: {
                    zIndex: 1,
                    boxShadow:
                      scrollPosition.y > 0
                        ? 'var(--mantine-shadow-shadow8)'
                        : 'none',
                  },
                }}
              >
                <Title
                  order={3}
                  size="h6"
                  c="neutral.7"
                  flex={1}
                  tt="uppercase"
                >
                  {tPackageDetails('item_details')}
                </Title>
                <Space mx="auto" />
                <Button
                  variant="subtle"
                  c="primary.6"
                  fw={700}
                  flex="none"
                  onClick={handleToggleAccordion}
                  rightSection={<ChevronUpPrimary width={24} height={24} />}
                  styles={{
                    section: {
                      rotate: activeAccordion.length > 0 ? '0deg' : '180deg',
                    },
                  }}
                >
                  {tActions(activeAccordion.length > 0 ? 'collapse' : 'expand')}
                </Button>
              </Group>
              <ScrollArea.Autosize
                scrollbars="y"
                onScrollPositionChange={onScrollPositionChange}
                viewportRef={viewportRef}
              >
                <Accordion
                  variant="separated"
                  multiple
                  value={activeAccordion}
                  onChange={setActiveAccordion}
                  px="md"
                  pb={{ base: '0', sm: 'md' }}
                  styles={{
                    item: {
                      backgroundColor: 'white',
                      borderColor: 'var(--mantine-color-neutral-3)',
                    },
                  }}
                >
                  {fields.map((field, index) => {
                    return (
                      <Accordion.Item value={field.id} key={field.id}>
                        <Accordion.Control
                          icon={
                            <Image
                              src={`${assetPrefix}/static/images/packages.webp`}
                              alt="packages"
                              width={24}
                              height={24}
                            />
                          }
                          styles={{
                            icon: { marginRight: rem(8) },
                            control: {
                              paddingLeft: rem(12),
                              paddingRight: rem(12),
                            },
                            label: {
                              paddingTop: rem(12),
                              paddingBottom: rem(12),
                            },
                          }}
                        >
                          <Group align="center" gap={rem(16)}>
                            <Text
                              size="xs"
                              c="neutral.6"
                              fw={700}
                              tt="uppercase"
                            >
                              {tPackageDetails('item_index', {
                                index: index + 1,
                              })}
                            </Text>
                            <Button
                              disabled={fields.length <= 1}
                              variant="light"
                              color="red"
                              fw={700}
                              h={rem(32)}
                              onClick={(e) => {
                                e.stopPropagation();
                                remove(index);
                              }}
                            >
                              {tActions('delete')}
                            </Button>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel
                          styles={{
                            content: {
                              padding: 0,
                              paddingRight: rem(12),
                              paddingBottom: rem(12),
                              paddingLeft: rem(12),
                            },
                          }}
                        >
                          <Stack gap="sm" w="100%">
                            <SimpleGrid
                              cols={{ base: 1, md: 2 }}
                              spacing={rem(8)}
                            >
                              <Controller
                                control={control}
                                name={`package_detail.${index}.name`}
                                render={({ field: { onChange, value } }) => (
                                  <FloatingLabelInput
                                    error={
                                      errors.package_detail?.[index]?.name
                                        ?.message
                                    }
                                    label={tPackageDetails('item_name')}
                                    value={value}
                                    onChange={(value) => onChange(value)}
                                  />
                                )}
                              />
                              <Controller
                                control={control}
                                name={`package_detail.${index}.weight`}
                                render={({ field: { onChange, value } }) => (
                                  <FloatingLabelNumberInput
                                    hideControls
                                    error={
                                      errors.package_detail?.[index]?.weight
                                        ?.message
                                    }
                                    value={value}
                                    onChange={(v) =>
                                      onChange(parseFloat(isNaN(v) ? 0 : v))
                                    }
                                    label={tPackageDetails('weight_kg')}
                                    withAsterisk
                                  />
                                )}
                              />
                            </SimpleGrid>

                            <Grid
                              gutter={rem(8)}
                              styles={{
                                inner: {
                                  alignItems: 'flex-start',
                                },
                              }}
                            >
                              <Grid.Col span={{ base: 12, md: 7 }}>
                                <SimpleGrid cols={3} spacing={rem(8)}>
                                  <Controller
                                    control={control}
                                    name={`package_detail.${index}.length`}
                                    render={({
                                      field: { onChange, value },
                                    }) => (
                                      <FloatingLabelNumberInput
                                        hideControls
                                        error={
                                          errors.package_detail?.[index]?.length
                                            ?.message
                                        }
                                        // min={1}
                                        step={1}
                                        // max={300}
                                        value={value}
                                        onChange={(v) =>
                                          onChange(parseFloat(isNaN(v) ? 0 : v))
                                        }
                                        label={tPackageDetails('length_cm')}
                                        withAsterisk
                                      />
                                    )}
                                  />

                                  <Controller
                                    control={control}
                                    name={`package_detail.${index}.width`}
                                    render={({
                                      field: { onChange, value },
                                    }) => (
                                      <FloatingLabelNumberInput
                                        hideControls
                                        // min={1}
                                        step={1}
                                        // max={300}
                                        error={
                                          errors.package_detail?.[index]?.width
                                            ?.message
                                        }
                                        value={value}
                                        onChange={(v) =>
                                          onChange(parseFloat(isNaN(v) ? 0 : v))
                                        }
                                        label={tPackageDetails('width_cm')}
                                        withAsterisk
                                      />
                                    )}
                                  />

                                  <Controller
                                    control={control}
                                    name={`package_detail.${index}.height`}
                                    render={({
                                      field: { onChange, value },
                                    }) => (
                                      <FloatingLabelNumberInput
                                        hideControls
                                        // min={1}
                                        step={1}
                                        // max={300}
                                        error={
                                          errors.package_detail?.[index]?.height
                                            ?.message
                                        }
                                        value={value}
                                        onChange={(v) =>
                                          onChange(parseFloat(isNaN(v) ? 0 : v))
                                        }
                                        label={tPackageDetails('height_cm')}
                                        withAsterisk
                                      />
                                    )}
                                  />
                                </SimpleGrid>
                              </Grid.Col>
                              <Grid.Col span={{ base: 12, md: 5 }}>
                                <Group
                                  mt={rem(7)}
                                  wrap="nowrap"
                                  gap={rem(8)}
                                  align="center"
                                  justify="flex-end"
                                >
                                  <Text
                                    size="sm"
                                    c="neutral.7"
                                    fw={500}
                                    styles={{
                                      root: { whiteSpace: 'nowrap' },
                                    }}
                                  >
                                    {tPackageDetails('quantity')}
                                  </Text>
                                  <Controller
                                    control={control}
                                    name={`package_detail.${index}.quantity`}
                                    render={({
                                      field: { onChange, value },
                                    }) => (
                                      <NumberInput
                                        error={
                                          errors.package_detail?.[index]
                                            ?.quantity?.message
                                        }
                                        value={value}
                                        isAllowed={({ value }) => {
                                          if (Number(value) > 100) {
                                            setValue(
                                              `package_detail.${index}.quantity`,
                                              100
                                            );
                                          }
                                          return Number(value) <= 100;
                                        }}
                                        onChange={(value) => {
                                          onChange(Number(value));
                                        }}
                                        hideControls
                                        size="md"
                                        w="100%"
                                        maw={rem(200)}
                                        fw={500}
                                        min={1}
                                        step={1}
                                        max={100}
                                        allowDecimal={false}
                                        leftSection={
                                          <ActionIcon
                                            size={40}
                                            variant="light"
                                            disabled={value <= 1}
                                            onClick={() => {
                                              setValue(
                                                `package_detail.${index}.quantity`,
                                                value - 1
                                              );
                                            }}
                                          >
                                            <Minus size={24} />
                                          </ActionIcon>
                                        }
                                        rightSection={
                                          <ActionIcon
                                            size={40}
                                            variant="light"
                                            disabled={value >= 100}
                                            onClick={() => {
                                              setValue(
                                                `package_detail.${index}.quantity`,
                                                value + 1
                                              );
                                            }}
                                          >
                                            <Plus size={24} />
                                          </ActionIcon>
                                        }
                                        styles={{
                                          required: { fontSize: 15 },
                                          error: {
                                            fontSize: 12,
                                            fontWeight: 500,
                                          },
                                          input: {
                                            paddingLeft: rem(40),
                                            paddingRight: rem(40),
                                            height: rem(40),
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis',
                                            overflowX: 'hidden',
                                            textAlign: 'center',
                                          },
                                          section: {
                                            width: rem(40),
                                          },
                                        }}
                                      />
                                    )}
                                  />
                                </Group>
                              </Grid.Col>
                            </Grid>

                            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                              <Group gap={rem(6)} align="center" wrap="nowrap">
                                <Text size="sm" c="neutral.7" fw={500}>
                                  {tPackageDetails('volumetric_weight')}{' '}
                                  <InfoTooltip
                                    label={tPackageDetails('volumetric_weight')}
                                    onClick={() => {
                                      openCustomConfirmModal({
                                        title: tPackageDetails(
                                          'what_is_volumetric_weight'
                                        ),
                                        children: tPackageDetails.rich(
                                          'volumetric_weight_is',
                                          {
                                            dim_factor: formatNumber(
                                              estBillableWeightData?.dim_factor ??
                                                5000
                                            ),
                                          }
                                        ),
                                        confirmLabel: tActions('got_it'),
                                      });
                                    }}
                                  />
                                </Text>
                                <Space flex={1} />
                                <Text
                                  size="sm"
                                  c="neutral.7"
                                  fw={500}
                                  styles={{ root: { whiteSpace: 'nowrap' } }}
                                >
                                  {formatNumber(
                                    estBillableWeightData
                                      ?.package_weight_details?.[index]
                                      ?.volumetric_weight ?? 0
                                  )}{' '}
                                  (Kg)
                                </Text>
                              </Group>

                              <Group
                                gap={rem(6)}
                                align="center"
                                wrap="nowrap"
                                pos="relative"
                              >
                                <Divider
                                  display={{ base: 'none', md: 'block' }}
                                  orientation="vertical"
                                  pos="absolute"
                                  top={0}
                                  bottom={0}
                                  left={rem(-8)}
                                />
                                <Text size="sm" c="secondary.5" fw={700}>
                                  {tPackageDetails('chargeable_weight')}{' '}
                                  <InfoTooltip
                                    label={tPackageDetails('chargeable_weight')}
                                    onClick={() => {
                                      openCustomConfirmModal({
                                        title: tPackageDetails(
                                          'what_is_chargeable_weight'
                                        ),
                                        children: tPackageDetails.rich(
                                          'chargeable_weight_is'
                                        ),
                                        confirmLabel: tActions('got_it'),
                                      });
                                    }}
                                  />
                                </Text>
                                <Space flex={1} />
                                <Text
                                  size="sm"
                                  c="secondary.5"
                                  fw={700}
                                  styles={{ root: { whiteSpace: 'nowrap' } }}
                                >
                                  {formatNumber(
                                    estBillableWeightData
                                      ?.package_weight_details?.[index]
                                      ?.billable_weight ?? 0
                                  )}{' '}
                                  (Kg)
                                </Text>
                              </Group>
                            </SimpleGrid>
                          </Stack>
                        </Accordion.Panel>
                        {!activeAccordion.includes(field.id) ? (
                          <Text size="md" fw={500} c="neutral.7" p="sm">
                            {tPackageDetails.rich('package_details_summary', {
                              name: parseSafeValue(
                                getValues(`package_detail.${index}.name`)
                              ),
                              weight: parseSafeValue(
                                getValues(`package_detail.${index}.weight`)
                              ),
                              length: parseSafeValue(
                                getValues(`package_detail.${index}.length`)
                              ),
                              width: parseSafeValue(
                                getValues(`package_detail.${index}.width`)
                              ),
                              height: parseSafeValue(
                                getValues(`package_detail.${index}.height`)
                              ),
                              quantity: parseSafeValue(
                                getValues(`package_detail.${index}.quantity`)
                              ),
                              volumetric_weight: parseSafeValue(
                                estBillableWeightData?.package_weight_details?.[
                                  index
                                ]?.volumetric_weight
                              ),
                              chargeable_weight: parseSafeValue(
                                estBillableWeightData?.package_weight_details?.[
                                  index
                                ]?.billable_weight
                              ),
                              divider: () => (
                                <Divider
                                  orientation="vertical"
                                  h={rem(14)}
                                  mx={rem(4)}
                                  display="inline"
                                />
                              ),
                            })}
                          </Text>
                        ) : null}
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              </ScrollArea.Autosize>
              <Center
                p="md"
                styles={{
                  root: {
                    zIndex: 1,
                    boxShadow:
                      viewportRef.current?.clientHeight &&
                      viewportRef.current?.scrollHeight &&
                      viewportRef.current?.scrollHeight -
                        viewportRef.current?.clientHeight !==
                        scrollPosition.y
                        ? 'var(--mantine-shadow-shadow4Revert)'
                        : 'none',
                  },
                }}
              >
                <Button
                  fullWidth
                  h={rem(40)}
                  fw={700}
                  maw={{ base: '100%', sm: rem(343) }}
                  variant="light"
                  disabled={isEstimating}
                  onClick={handleAddPackageItem}
                  leftSection={<AddPrimary width={16} height={16} />}
                >
                  {tPackageDetails('add_an_item')}
                </Button>
              </Center>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 'content' }}>
            <Stack flex="none" w={{ base: '100%', sm: rem(332), md: rem(440) }}>
              <Card radius="md">
                <Group
                  align="center"
                  wrap="nowrap"
                  gap={rem(8)}
                  justify="space-between"
                >
                  <Title
                    order={3}
                    size="h6"
                    lh={rem(36)}
                    c="neutral.7"
                    flex={1}
                    tt="uppercase"
                  >
                    {tPackageDetails('package_information')}
                  </Title>
                  <Space mx="auto" />
                  {/* <Button
                    variant="subtle"
                    c="primary.6"
                    fw={700}
                    flex="none"
                    onClick={toggleItemDetails}
                    rightSection={<ChevronUpPrimary width={24} height={24} />}
                    styles={{
                      section: {
                        rotate: openedItemDetails ? '0deg' : '180deg',
                      },
                    }}
                  >
                    {tActions(openedItemDetails ? 'collapse' : 'expand')}
                  </Button> */}
                </Group>

                {/* <Collapse in={openedItemDetails}> */}
                <Stack mt="md" gap="sm" w="100%">
                  <Button
                    variant="default"
                    size="md"
                    fullWidth
                    fw={500}
                    c={
                      nationWidePathInfo?.item_description
                        ? 'neutral.7'
                        : 'neutral.5'
                    }
                    px="md"
                    h={rem(56)}
                    justify="space-between"
                    rightSection={<AccordionChevron size={rem(24)} />}
                    styles={{
                      label: {
                        display: 'block',
                        height: 'auto',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflowX: 'hidden',
                      },
                    }}
                    onClick={() => {
                      openContextModal({
                        id: ModalName.ItemType,
                        modal: ModalName.ItemType,
                        innerProps: {
                          initialValue: nationWidePathInfo?.item_description,
                          onConfirm: (value) => {
                            setValue('item_description', value);
                          },
                        },
                        fullScreen: isMobile,
                        withCloseButton: false,
                        scrollAreaComponent: ScrollArea.Autosize,
                        styles: {
                          content: {
                            backgroundColor: 'var(--mantine-color-neutral-0)',
                          },
                          body: {
                            position: 'relative',
                            padding: 0,
                          },
                        },
                      });
                    }}
                  >
                    <Text fw={500} fz={14}>
                      {nationWidePathInfo?.item_description ??
                        tPackageDetails('item_type')}
                    </Text>
                  </Button>
                  <Controller
                    control={control}
                    name="item_description"
                    render={({ field: { onChange, value } }) => (
                      <FloatingLabelInput
                        error={errors.item_description?.message}
                        // label={tPackageDetails('item_type')}
                        style={{ display: 'none' }}
                        value={value}
                        onChange={(value) => onChange(value)}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="item_order_id"
                    render={({ field: { onChange, value } }) => (
                      <FloatingLabelInput
                        error={errors.item_order_id?.message}
                        label={tPackageDetails('tracking_number')}
                        value={value}
                        onChange={(value) => onChange(value)}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="item_name"
                    render={({ field: { onChange, value } }) => (
                      <FloatingLabelInput
                        error={errors.item_name?.message}
                        label={tPackageDetails('item_name')}
                        value={value}
                        onChange={(value) => onChange(value)}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="item_value"
                    render={({ field: { onChange, value } }) => (
                      <FloatingLabelNumberInput
                        hideControls
                        value={value}
                        onChange={(value) => onChange(Number(value))}
                        error={errors.item_value?.message}
                        label={tPackageDetails('item_value')}
                        allowNegative={false}
                      />
                    )}
                  />
                  <Text size="xs" c="neutral.5" fw={500} mt={rem(-8)}>
                    {tPackageDetails.rich('insurance_policy', {
                      link: (chunks) => (
                        <Link
                          href="https://ahamove.com/quy-dinh-bao-hiem-hang-hoa-cua-dich-vu-giao-hang-lien-tinh"
                          target="_blank"
                          passHref
                        >
                          <Text
                            size="xs"
                            c="secondary.5"
                            fw={500}
                            component="span"
                          >
                            {chunks}
                          </Text>
                        </Link>
                      ),
                    })}
                  </Text>

                  <Stack gap={rem(8)}>
                    <Title order={3} size="h6" c="neutral.7" flex={1}>
                      {tCommon('image')}
                    </Title>

                    <Controller
                      control={control}
                      name="package_images"
                      render={({ field: { onChange, value } }) => (
                        <UploadPhoto
                          images={value ?? []}
                          onChange={(value) => onChange(value)}
                        />
                      )}
                    />
                  </Stack>
                </Stack>
                {/* </Collapse> */}
              </Card>

              <Card
                radius="md"
                pos={{ base: 'fixed', sm: 'relative' }}
                w="100%"
                left={0}
                bottom={0}
                classNames={{
                  root: 'z-30 sm:z-[1] !shadow-buttonDock sm:!shadow-none',
                }}
              >
                <Stack gap="md">
                  <SimpleGrid cols={2} spacing={rem(8)}>
                    <Group
                      gap={rem(8)}
                      align="center"
                      justify="center"
                      pl="sm"
                      pr="xl"
                      wrap="nowrap"
                    >
                      <Weight className="size-5 flex-none" />
                      <Text
                        size="lg"
                        fw={700}
                        c={
                          estBillableWeightData?.total_volumetric_weight &&
                          estBillableWeightData?.total_volumetric_weight > 0
                            ? 'neutral.7'
                            : 'neutral.4'
                        }
                        lts={0.5}
                        truncate
                        title={formatNumber(
                          estBillableWeightData?.total_volumetric_weight ?? 0
                        )}
                      >
                        {formatNumber(
                          estBillableWeightData?.total_volumetric_weight ?? 0
                        )}
                      </Text>
                      <Text
                        size="xs"
                        fw={700}
                        c={
                          estBillableWeightData?.total_volumetric_weight &&
                          estBillableWeightData?.total_volumetric_weight > 0
                            ? 'neutral.7'
                            : 'neutral.4'
                        }
                        lts={0.5}
                        component="span"
                        tt="uppercase"
                        styles={{ root: { verticalAlign: 'middle' } }}
                      >
                        kg
                      </Text>
                    </Group>
                    <Group
                      gap={rem(8)}
                      align="center"
                      justify="space-between"
                      ml={rem(-12)}
                    >
                      <Divider size="sm" orientation="vertical" mr={rem(4)} />
                      <BoxBlue className="size-5 flex-none" />
                      <Text
                        size="lg"
                        fw={700}
                        c={
                          Object.values(totalLengthWidthHeight).some(
                            (v) => v > 0
                          )
                            ? 'neutral.7'
                            : 'neutral.4'
                        }
                        lts={0.5}
                        flex={1}
                        truncate
                        title={`${formatNumber(totalLengthWidthHeight.length)} x ${formatNumber(totalLengthWidthHeight.width)} x ${formatNumber(totalLengthWidthHeight.height)}`}
                      >
                        {`${formatNumber(totalLengthWidthHeight.length)} x ${formatNumber(totalLengthWidthHeight.width)} x ${formatNumber(totalLengthWidthHeight.height)}`}
                      </Text>
                      <Text
                        size="xs"
                        fw={700}
                        c={
                          Object.values(totalLengthWidthHeight).some(
                            (v) => v > 0
                          )
                            ? 'neutral.7'
                            : 'neutral.4'
                        }
                        lts={0.5}
                        component="span"
                        tt="uppercase"
                        flex="none"
                        styles={{ root: { verticalAlign: 'middle' } }}
                      >
                        CM
                      </Text>
                    </Group>
                  </SimpleGrid>
                  <Divider />
                  <Group align="center" wrap="nowrap" mt={rem(-8)} gap={rem(4)}>
                    <Text
                      size="sm"
                      fw={700}
                      c="neutral.6"
                      styles={{ root: { whiteSpace: 'nowrap' } }}
                    >
                      {tPackageDetails('total_weight')}
                    </Text>
                    <InfoTooltip
                      label={tPackageDetails('total_weight')}
                      onClick={() => {
                        openCustomConfirmModal({
                          title: tPackageDetails('total_weight'),
                          children: tPackageDetails('total_weight_is'),
                          confirmLabel: tActions('got_it'),
                        });
                      }}
                    />
                    <Space flex={1} />
                    {isEstimating ? (
                      <Skeleton my={4} height={24} width={72} />
                    ) : (
                      <Title
                        order={3}
                        size="h2"
                        c={
                          estBillableWeightData?.total_billable_weight &&
                          estBillableWeightData?.total_billable_weight > 0
                            ? 'primary.5'
                            : 'neutral.4'
                        }
                        styles={{
                          root: {
                            display: 'flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflowX: 'hidden',
                          },
                        }}
                        title={`${formatNumber(estBillableWeightData?.total_billable_weight)} kg`}
                      >
                        {formatNumber(
                          estBillableWeightData?.total_billable_weight
                        )}{' '}
                        kg
                      </Title>
                    )}
                  </Group>
                  <Space w="100%" h={rem(1)} />
                  <Button
                    fullWidth
                    size="md"
                    h={rem(48)}
                    disabled={
                      invalidPackageDetails ||
                      !isValid ||
                      estimateError ||
                      isEstimating
                    }
                    // loading={isEstimating || isValidating}
                    type="submit"
                  >
                    {tActions('confirm')}
                  </Button>
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>
        </Grid>
      </form>
    </Container>
  );
}
